import { create } from "zustand";
import {
  type NotificationChannel,
  type NotificationGroupKey,
  type StatusNotificationToggles,
  buildDefaultPreferences,
  buildDefaultGroupSounds,
} from "@/lib/notificationConfig";

// ─── localStorage key ────────────────────────────────────────────────────────

const STORAGE_KEY = "notificationPreferences";

// ─── Legacy localStorage keys (from old tableUIStore sound settings) ─────────

const LEGACY_KEYS = {
  soundQuoted: "soundQuoted",
  soundAwarded: "soundAwarded",
  soundExecuted: "soundExecuted",
  soundAffirmed: "soundAffirmed",
} as const;

// ─── Store Interface ─────────────────────────────────────────────────────────

interface NotificationPreferencesState {
  globalSoundEnabled: boolean;
  globalBellEnabled: boolean;
  globalBrowserEnabled: boolean;
  groupSounds: Record<NotificationGroupKey, string>;
  statusPreferences: Record<string, StatusNotificationToggles>;
}

interface NotificationPreferencesStore extends NotificationPreferencesState {
  setGlobalSoundEnabled: (enabled: boolean) => void;
  setGlobalBellEnabled: (enabled: boolean) => void;
  setGlobalBrowserEnabled: (enabled: boolean) => void;
  toggleStatusChannel: (
    status: string,
    channel: NotificationChannel,
  ) => void;
  setGroupSound: (groupKey: NotificationGroupKey, soundFile: string) => void;
  toggleAllInGroup: (
    groupKey: NotificationGroupKey,
    channel: NotificationChannel,
    enabled: boolean,
    statuses: string[],
  ) => void;
  resetToDefaults: () => void;
  initializeState: () => void;
}

/**
 * Check if the browser has granted notification permission.
 * Used to gate the browser master toggle default and on-load override.
 */
export function isBrowserNotificationPermissionGranted(): boolean {
  return (
    typeof Notification !== "undefined" &&
    Notification.permission === "granted"
  );
}

// ─── Persistence Helpers ─────────────────────────────────────────────────────

function saveToLocalStorage(state: NotificationPreferencesState): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      globalSoundEnabled: state.globalSoundEnabled,
      globalBellEnabled: state.globalBellEnabled,
      globalBrowserEnabled: state.globalBrowserEnabled,
      groupSounds: state.groupSounds,
      statusPreferences: state.statusPreferences,
    }),
  );
}

/**
 * Migrates old role-aware composite keys back to simple enum keys.
 * Collapses "Quoted (Received)" / "Quoted (Quote Sent)" → "Quoted"
 * and "Re-Sent (Sent)" / "Re-Sent (Received)" → "Re-Sent".
 */
function migrateRoleAwarePreferences(
  prefs: Record<string, StatusNotificationToggles>,
): Record<string, StatusNotificationToggles> {
  const migrated = { ...prefs };

  // Collapse role-aware Quoted variants → "Quoted"
  if (!migrated["Quoted"]) {
    const source = migrated["Quoted (Received)"] ?? migrated["Quoted (Quote Sent)"];
    if (source) migrated["Quoted"] = { ...source };
  }
  delete migrated["Quoted (Received)"];
  delete migrated["Quoted (Quote Sent)"];

  // Collapse role-aware Re-Sent variants → "Re-Sent"
  if (!migrated["Re-Sent"]) {
    const source = migrated["Re-Sent (Sent)"] ?? migrated["Re-Sent (Received)"];
    if (source) migrated["Re-Sent"] = { ...source };
  }
  delete migrated["Re-Sent (Sent)"];
  delete migrated["Re-Sent (Received)"];

  return migrated;
}

// ─── Store ───────────────────────────────────────────────────────────────────

const useNotificationPreferencesStore = create<NotificationPreferencesStore>(
  (set, get) => ({
    // Default state — browser defaults ON only if permission already granted
    globalSoundEnabled: false,
    globalBellEnabled: false,
    globalBrowserEnabled:
      isBrowserNotificationPermissionGranted(),
    groupSounds: buildDefaultGroupSounds(),
    statusPreferences: buildDefaultPreferences(),

    setGlobalSoundEnabled: (enabled) =>
      set((state) => {
        const newState = { ...state, globalSoundEnabled: enabled };
        saveToLocalStorage(newState);
        return { globalSoundEnabled: enabled };
      }),

    setGlobalBellEnabled: (enabled) =>
      set((state) => {
        const newState = { ...state, globalBellEnabled: enabled };
        saveToLocalStorage(newState);
        return { globalBellEnabled: enabled };
      }),

    setGlobalBrowserEnabled: (enabled) =>
      set((state) => {
        const newState = { ...state, globalBrowserEnabled: enabled };
        saveToLocalStorage(newState);
        return { globalBrowserEnabled: enabled };
      }),

    toggleStatusChannel: (status, channel) =>
      set((state) => {
        const currentPrefs = state.statusPreferences[status];
        if (!currentPrefs) return {};

        const updatedPreferences = {
          ...state.statusPreferences,
          [status]: {
            ...currentPrefs,
            [channel]: !currentPrefs[channel],
          },
        };
        const newState = { ...state, statusPreferences: updatedPreferences };
        saveToLocalStorage(newState);
        return { statusPreferences: updatedPreferences };
      }),

    setGroupSound: (groupKey, soundFile) =>
      set((state) => {
        const updatedGroupSounds = {
          ...state.groupSounds,
          [groupKey]: soundFile,
        };
        const newState = { ...state, groupSounds: updatedGroupSounds };
        saveToLocalStorage(newState);
        return { groupSounds: updatedGroupSounds };
      }),

    toggleAllInGroup: (groupKey, channel, enabled, statuses) =>
      set((state) => {
        const updatedPreferences = { ...state.statusPreferences };
        for (const status of statuses) {
          if (updatedPreferences[status]) {
            updatedPreferences[status] = {
              ...updatedPreferences[status],
              [channel]: enabled,
            };
          }
        }
        const newState = { ...state, statusPreferences: updatedPreferences };
        saveToLocalStorage(newState);
        return { statusPreferences: updatedPreferences };
      }),

    resetToDefaults: () =>
      set(() => {
        const defaults: NotificationPreferencesState = {
          globalSoundEnabled: false,
          globalBellEnabled: false,
          globalBrowserEnabled:
            isBrowserNotificationPermissionGranted(),
          groupSounds: buildDefaultGroupSounds(),
          statusPreferences: buildDefaultPreferences(),
        };
        saveToLocalStorage(defaults);
        return defaults;
      }),

    initializeState: () => {
      // 1. Try to load from new storage key
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as Partial<NotificationPreferencesState>;

          // Migrate old "Quoted" / "Re-Sent" keys to role-aware composite keys
          const migratedPrefs = parsed.statusPreferences
            ? migrateRoleAwarePreferences(parsed.statusPreferences)
            : undefined;

          // Ensure any new statuses added in config get default preferences
          if (migratedPrefs) {
            const defaults = buildDefaultPreferences();
            for (const key of Object.keys(defaults)) {
              if (!migratedPrefs[key]) {
                migratedPrefs[key] = defaults[key];
              }
            }
          }

          // If saved browser toggle is ON but permission is no longer granted, override to OFF
          const browserPermissionGranted =
            isBrowserNotificationPermissionGranted();
          const resolvedBrowserEnabled =
            parsed.globalBrowserEnabled && !browserPermissionGranted
              ? false
              : parsed.globalBrowserEnabled;

          const newState: Partial<NotificationPreferencesState> = {
            ...(parsed.globalSoundEnabled !== undefined && {
              globalSoundEnabled: parsed.globalSoundEnabled,
            }),
            ...(parsed.globalBellEnabled !== undefined && {
              globalBellEnabled: parsed.globalBellEnabled,
            }),
            ...(resolvedBrowserEnabled !== undefined && {
              globalBrowserEnabled: resolvedBrowserEnabled,
            }),
            ...(parsed.groupSounds && { groupSounds: parsed.groupSounds }),
            ...(migratedPrefs && { statusPreferences: migratedPrefs }),
          };

          set(newState);

          // Persist migrated state back to localStorage
          saveToLocalStorage({ ...get(), ...newState } as NotificationPreferencesState);
          return;
        } catch {
          // Corrupted data — fall through to migration
        }
      }

      // 2. Migration from legacy keys
      const legacyQuoted = localStorage.getItem(LEGACY_KEYS.soundQuoted);
      const legacyAwarded = localStorage.getItem(LEGACY_KEYS.soundAwarded);
      const legacyExecuted = localStorage.getItem(LEGACY_KEYS.soundExecuted);
      const legacyAffirmed = localStorage.getItem(LEGACY_KEYS.soundAffirmed);

      const hasLegacy =
        legacyQuoted || legacyAwarded || legacyExecuted || legacyAffirmed;

      if (hasLegacy) {
        const defaultGroupSounds = buildDefaultGroupSounds();

        // Map legacy sound selections to their new group sounds
        if (legacyQuoted) {
          try {
            defaultGroupSounds.actionRequired = JSON.parse(legacyQuoted);
          } catch { /* use default */ }
        }
        if (legacyAwarded) {
          try {
            defaultGroupSounds.waitingCounterparty = JSON.parse(legacyAwarded);
          } catch { /* use default */ }
        }
        if (legacyAffirmed) {
          try {
            defaultGroupSounds.noFurtherAction = JSON.parse(legacyAffirmed);
          } catch { /* use default */ }
        }

        const migratedState: NotificationPreferencesState = {
          globalSoundEnabled: true,
          globalBellEnabled: true,
          globalBrowserEnabled:
            isBrowserNotificationPermissionGranted(),
          groupSounds: defaultGroupSounds,
          statusPreferences: buildDefaultPreferences(),
        };

        saveToLocalStorage(migratedState);
        set(migratedState);
      }
      // If no saved data and no legacy keys, the store defaults are already correct
    },
  }),
);

export default useNotificationPreferencesStore;
